import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck, Loader2, CheckCircle2 } from 'lucide-react';
import StoreLayout, { WhatsAppFloat } from '../../components/store/StoreLayout';
import { useCart } from '../../contexts/CartContext';
import { formatRupee as fmt } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Checkout() {
  const { items, totalValue, clearCart } = useCart();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: '', mobile: '', email: '',
    address: '', pincode: '', notes: '',
  });
  const [otp, setOtp] = useState({ sent: false, code: '', verified: false, token: '', cooldown: 0 });
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const canSendOtp = /^\d{10}$/.test(form.mobile.replace(/\D/g, ''));

  const sendOtp = async () => {
    if (!canSendOtp) { toast.error('Enter a valid 10-digit mobile number'); return; }
    setSendingOtp(true);
    try {
      await axios.post(`${BACKEND_URL}/api/store/checkout/send-otp`, { mobile: form.mobile });
      setOtp(o => ({ ...o, sent: true, verified: false, cooldown: 30 }));
      toast.success('OTP sent to your phone');
      const timer = setInterval(() => setOtp(o => {
        if (o.cooldown <= 1) { clearInterval(timer); return { ...o, cooldown: 0 }; }
        return { ...o, cooldown: o.cooldown - 1 };
      }), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send OTP');
    } finally { setSendingOtp(false); }
  };

  const verifyOtp = async () => {
    if (!/^\d{4,6}$/.test(otp.code)) { toast.error('Enter the OTP code'); return; }
    setVerifying(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/store/checkout/verify-otp`, { mobile: form.mobile, code: otp.code });
      setOtp(o => ({ ...o, verified: true, token: r.data.token }));
      toast.success('Phone verified');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Verification failed');
    } finally { setVerifying(false); }
  };

  const submit = async () => {
    if (!otp.verified) { toast.error('Please verify your phone number first'); return; }
    if (items.length === 0) { toast.error('Your cart is empty'); return; }
    if (!form.name.trim() || !form.address.trim() || !/^\d{6}$/.test(form.pincode.trim())) {
      toast.error('Please fill name, address, and a 6-digit pincode');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/store/checkout`, {
        name: form.name,
        mobile: form.mobile,
        otp_token: otp.token,
        email: form.email,
        address: form.address,
        pincode: form.pincode,
        notes: form.notes,
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
      });
      clearCart();
      navigate(`/order/${r.data.tracking_token}`, { replace: true, state: { justPlaced: true, orderId: r.data.order_id } });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not place your order');
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <StoreLayout>
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-semibold">Your cart is empty.</h1>
          <p className="text-stone-500 mt-2">Add products before checking out.</p>
          <Link to="/shop" className="inline-block mt-6 bg-amber-700 hover:bg-amber-800 text-white px-6 py-2.5 rounded-full text-sm">Browse the shop</Link>
        </div>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <WhatsAppFloat />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <Link to="/cart" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-amber-800 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to cart
        </Link>
        <h1 className="text-3xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>Checkout</h1>
        <p className="text-sm text-stone-500 mt-1">Only your contact + address are needed now. We&apos;ll call to confirm delivery and payment.</p>

        <div className="mt-8 grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            {/* Contact */}
            <fieldset className="border border-stone-200 rounded-xl p-5">
              <legend className="px-2 text-xs uppercase tracking-widest text-stone-500">Contact</legend>
              <div className="grid gap-4">
                <label className="block">
                  <span className="text-xs text-stone-600">Full name *</span>
                  <input value={form.name} onChange={setField('name')} data-testid="co-name" className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm" placeholder="Your name" />
                </label>
                <div className="grid md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs text-stone-600">Mobile (India) *</span>
                    <div className="mt-1 flex gap-2">
                      <input value={form.mobile} onChange={setField('mobile')} data-testid="co-mobile" disabled={otp.verified} className="flex-1 border border-stone-300 rounded-lg px-3 py-2 text-sm disabled:bg-stone-50 disabled:text-stone-500" placeholder="10-digit mobile" />
                      {!otp.verified && (
                        <button onClick={sendOtp} disabled={!canSendOtp || sendingOtp || otp.cooldown > 0} data-testid="co-send-otp" className="text-xs bg-stone-900 hover:bg-black text-white px-3 rounded-lg disabled:opacity-50">
                          {sendingOtp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : otp.cooldown > 0 ? `${otp.cooldown}s` : otp.sent ? 'Resend' : 'Send OTP'}
                        </button>
                      )}
                      {otp.verified && <CheckCircle2 className="w-5 h-5 self-center text-lime-600" data-testid="co-mobile-verified" />}
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-xs text-stone-600">Email (optional)</span>
                    <input value={form.email} onChange={setField('email')} data-testid="co-email" type="email" className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm" placeholder="you@example.com" />
                  </label>
                </div>
                {otp.sent && !otp.verified && (
                  <div className="flex gap-2 items-center bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <ShieldCheck className="w-4 h-4 text-amber-700" />
                    <input value={otp.code} onChange={e => setOtp(o => ({ ...o, code: e.target.value }))} data-testid="co-otp" placeholder="Enter OTP" className="flex-1 border border-amber-300 rounded px-2 py-1 text-sm bg-white" />
                    <button onClick={verifyOtp} disabled={verifying} data-testid="co-verify-otp" className="text-xs bg-amber-700 hover:bg-amber-800 text-white px-3 py-1.5 rounded disabled:opacity-50">
                      {verifying ? 'Verifying…' : 'Verify'}
                    </button>
                  </div>
                )}
              </div>
            </fieldset>

            {/* Address */}
            <fieldset className="border border-stone-200 rounded-xl p-5">
              <legend className="px-2 text-xs uppercase tracking-widest text-stone-500">Delivery address</legend>
              <label className="block">
                <span className="text-xs text-stone-600">Full address *</span>
                <textarea value={form.address} onChange={setField('address')} data-testid="co-address" rows="3" className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm" placeholder="Flat / house, street, area, landmark, city" />
              </label>
              <label className="block mt-3 max-w-xs">
                <span className="text-xs text-stone-600">Pincode *</span>
                <input value={form.pincode} onChange={setField('pincode')} data-testid="co-pincode" className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm" placeholder="6-digit pincode" maxLength={6} />
              </label>
              <label className="block mt-3">
                <span className="text-xs text-stone-600">Notes (optional)</span>
                <textarea value={form.notes} onChange={setField('notes')} data-testid="co-notes" rows="2" className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm" placeholder="Preferred delivery time, gate code, special instructions…" />
              </label>
            </fieldset>
          </div>

          {/* Summary sidebar */}
          <div className="md:col-span-1">
            <div className="border border-stone-200 rounded-xl p-5 sticky top-20">
              <div className="text-xs uppercase tracking-widest text-stone-500 mb-3">Order summary</div>
              <div className="space-y-2 text-sm max-h-64 overflow-auto">
                {items.map(i => (
                  <div key={i.product_id} className="flex justify-between gap-2">
                    <span className="text-stone-700 truncate">{i.product_name} <span className="text-[11px] text-stone-500">×{i.quantity}</span></span>
                    <span className="tabular-nums text-stone-900">{fmt(i.quantity * i.selling_price)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-3 border-t border-stone-200 flex justify-between text-sm">
                <span className="text-stone-600">Subtotal</span>
                <span className="tabular-nums font-semibold" data-testid="co-subtotal">{fmt(totalValue)}</span>
              </div>
              <div className="mt-2 text-[11px] text-stone-500 leading-relaxed">
                Delivery + payment mode discussed on confirmation call. No payment collected here.
              </div>
              <button
                onClick={submit}
                disabled={submitting || !otp.verified}
                data-testid="co-submit"
                className="w-full mt-5 bg-amber-700 hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-full text-sm font-medium transition"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Place order'}
              </button>
              {!otp.verified && <p className="text-[10px] text-amber-800 mt-2 text-center">Verify your mobile to place the order</p>}
            </div>
          </div>
        </div>
      </div>
    </StoreLayout>
  );
}
